/**
 * Hook that detects when a WebSocket reconnection attempt has exceeded
 * a timeout threshold, signalling the user should take manual action.
 *
 * Returns `true` once `connectionStatus` has been "reconnecting" for
 * longer than `timeoutMs`. Resets to `false` when status changes away
 * from "reconnecting".
 */

import { useEffect, useState } from "react";

type ConnectionStatus = "connected" | "reconnecting";

export function useWsReconnectTimeout(
  connectionStatus: ConnectionStatus,
  timeoutMs = 30_000,
): boolean {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (connectionStatus !== "reconnecting") {
      setTimedOut(false);
      return;
    }

    const t = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(t);
  }, [connectionStatus, timeoutMs]);

  return timedOut;
}
